import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { DatePicker } from '@/components/ui/DatePicker';
import { Select } from '@/components/ui/Select';
import type { AllocatedClass } from '../../types/teacher';
import { allotmentLabel } from '../../utils';

interface TimetableHeaderBarProps {
  allocatedClasses?: AllocatedClass[];
  /** The allotment the grid is narrowed to; `''` shows every class. */
  classFilterId: string;
  onClassFilterChange: (id: string) => void;
  weekRangeText: string;
  onPrevWeek: () => void;
  onNextWeek: () => void;
  onThisWeek: () => void;
  isCurrentWeek: boolean;
  /** The day the grid is centred on, `YYYY-MM-DD`. */
  pickedDate: string;
  /** Jumps straight to any day — a timetable that starts in three weeks is
   *  otherwise three clicks of an arrow away. */
  onPickDate: (date: string) => void;
}

export function TimetableHeaderBar({
  allocatedClasses = [],
  classFilterId,
  onClassFilterChange,
  weekRangeText,
  onPrevWeek,
  onNextWeek,
  onThisWeek,
  isCurrentWeek,
  pickedDate,
  onPickDate,
}: TimetableHeaderBarProps) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-ink tracking-tight">
          Class Timetable
        </h1>
        <p className="text-xs sm:text-sm text-ink-3 font-medium mt-1">
          View and manage your class schedule.
        </p>
      </div>

      {/* Right Controls: Class Dropdown + Week Range & Navigation */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Class Selector Dropdown */}
        {/* One option per allotment, named with its subject: a teacher with two
            subjects in 8-A otherwise sees "8-A" twice and cannot tell them apart. */}
        {allocatedClasses.length > 0 && (
          <div className="min-w-[140px] sm:min-w-[170px]">
            <Select
              label="Class"
              options={[
                { value: '', label: 'All my classes' },
                ...allocatedClasses.map((ac) => ({ value: ac.id, label: allotmentLabel(ac) })),
              ]}
              value={classFilterId}
              onChange={(event) => onClassFilterChange(event.target.value)}
              className="h-9 text-sm font-semibold"
            />
          </div>
        )}

        {/* Unified Week Date Picker & Navigation Controls */}
        <div className="flex flex-col gap-1">
          <span className="text-[12px] font-semibold text-slate-600 dark:text-slate-400 tracking-wide">
            Week
          </span>
          <div className="flex items-center gap-1.5">
            <div className="min-w-[195px] sm:min-w-[215px]">
              <DatePicker
                label="Week"
                hideLabel
                size="sm"
                value={pickedDate}
                displayValue={weekRangeText || undefined}
                icon={<Calendar className="h-3.5 w-3.5 text-indigo-400 shrink-0" />}
                onChange={(event) => {
                  if (event.target.value) onPickDate(event.target.value);
                }}
                className="h-9 px-3 rounded-[10px] border border-line bg-surface text-sm font-semibold text-ink shadow-2xs hover:border-slate-300 dark:hover:border-slate-600 transition-colors cursor-pointer"
              />
            </div>

            <div className="flex items-center gap-1 ml-0.5">
              <button
                type="button"
                onClick={onPrevWeek}
                title="Previous week"
                className="flex h-9 w-9 items-center justify-center rounded-[10px] border border-line bg-surface text-ink-3 hover:text-ink transition-colors cursor-pointer shadow-2xs"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={onNextWeek}
                title="Next week"
                className="flex h-9 w-9 items-center justify-center rounded-[10px] border border-line bg-surface text-ink-3 hover:text-ink transition-colors cursor-pointer shadow-2xs"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={onThisWeek}
                className={`h-9 px-3.5 rounded-[10px] text-sm font-bold transition-all cursor-pointer shadow-xs ${
                  isCurrentWeek
                    ? 'bg-indigo-600 text-white'
                    : 'border border-line bg-surface text-ink-2 hover:text-ink'
                }`}
              >
                Today
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
